import { getPhoneCountryById } from "@/lib/countryDialCodes";

/**
 * Formatage téléphone (FR + international courant) pour tout le SaaS.
 * — Affichage : espaces lisibles (06 12 34 56 78, +33 6 12 34 56 78).
 * — Saisie : même logique en direct pour limiter les formats bruts.
 */

/** Champ produit dont la valeur est un numéro de téléphone. */
export function isPhoneLikeChampName(nom) {
  const k = String(nom || "").trim();
  return /t[ée]l|phone|portable|mobile|num[ée]ro|fax/i.test(k) && !/foyer|personnes|€/i.test(k);
}

function digitsOnly(s) {
  return String(s ?? "").replace(/\D/g, "");
}

/** Groupe 10 chiffres nationaux FR 0XXXXXXXXX par paquets 2 2 2 2 2 */
function formatFrNationalDigits(d) {
  const x = d.slice(0, 10);
  if (x.length <= 2) return x;
  const pairs = [];
  pairs.push(x.slice(0, 2));
  for (let i = 2; i < x.length; i += 2) {
    pairs.push(x.slice(i, i + 2));
  }
  return pairs.join(" ");
}

/** +33 + 9 chiffres (sans 0 initial national) */
function formatFrIntl33Digits(digitsAfter33) {
  const n = digitsAfter33.slice(0, 9);
  if (!n.length) return "+33 ";
  const first = n.slice(0, 1);
  const rest = n.slice(1);
  const grouped = rest.length ? rest.match(/.{1,2}/g)?.join(" ") ?? rest : "";
  return grouped ? `+33 ${first} ${grouped}` : `+33 ${first}`;
}

/**
 * Valeur affichée / relue : espaces cohérents sans changer les chiffres significatifs.
 */
export function formatPhoneForDisplay(raw) {
  if (raw == null) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";

  const digits = digitsOnly(trimmed);
  if (!digits) return trimmed;

  // Stocké sans + : 33612345678
  if (digits.length === 11 && digits.startsWith("33")) {
    return formatFrIntl33Digits(digits.slice(2));
  }

  // +33 … ou 0033…
  if (trimmed.startsWith("+") || trimmed.startsWith("00")) {
    let d = digits;
    if (d.startsWith("00")) d = d.slice(2);
    if (d.startsWith("33")) {
      return formatFrIntl33Digits(d.slice(2));
    }
    // Autres indicatifs : +CC puis groupes de 3
    if (trimmed.startsWith("+") && d.length > 0) {
      const cc = d.slice(0, Math.min(3, d.length));
      const tail = d.slice(cc.length);
      if (!tail.length) return `+${cc}`;
      const chunks = tail.match(/.{1,3}/g) ?? [tail];
      return `+${cc} ${chunks.join(" ")}`.trim();
    }
  }

  // National FR 0X XX XX XX XX
  if (digits.startsWith("0") && digits.length <= 11) {
    return formatFrNationalDigits(digits);
  }

  return trimmed;
}

/**
 * Saisie contrôlée : reformate au fil de la frappe (FR national ou +33…).
 */
export function formatPhoneInputLive(raw) {
  const t = String(raw ?? "");
  if (!t.trim()) return "";

  const startsPlus = t.trimStart().startsWith("+");
  let digits = digitsOnly(t);

  if (startsPlus) {
    if (digits.startsWith("33")) {
      return formatFrIntl33Digits(digits.slice(2));
    }
    if (digits.length === 0) return "+";
    const ccLen = Math.min(3, digits.length);
    const cc = digits.slice(0, ccLen);
    const tail = digits.slice(ccLen);
    if (!tail.length) return `+${cc}`;
    const chunks = tail.match(/.{1,3}/g) ?? [tail];
    return `+${cc} ${chunks.join(" ")}`.trim();
  }

  if (digits.startsWith("0")) {
    return formatFrNationalDigits(digits);
  }

  // Saisie sans + : interpréter comme national si ça ressemble à un mobile/fixe FR
  if (digits.length > 0 && digits.length <= 10 && digits[0] === "0") {
    return formatFrNationalDigits(digits);
  }

  return t;
}

/**
 * Conversion E.164 sans exception (appels, stockage dossier).
 * Accepte 06…, +33…, 0033…, 33XXXXXXXXX, mobile 9 chiffres sans 0.
 */
export function tryNormalizePhoneToE164(raw) {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return "";

  const compact = trimmed.replace(/[\s\u00a0._-]/g, "");
  if (compact.startsWith("+")) {
    const d = compact.slice(1).replace(/\D/g, "");
    return d.length >= 8 && d.length <= 15 ? `+${d}` : "";
  }
  if (compact.startsWith("00")) {
    const d = compact.slice(2).replace(/\D/g, "");
    return d.length >= 8 && d.length <= 15 ? `+${d}` : "";
  }

  const only = compact.replace(/\D/g, "");
  if (/^0\d{9}$/.test(only)) return `+33${only.slice(1)}`;
  if (/^[67]\d{8}$/.test(only)) return `+33${only}`;
  if (/^33\d{9}$/.test(only)) return `+${only}`;

  return "";
}

/**
 * E.164 strict : le numéro doit être en format international (+indicatif).
 */
export function normalizeInternationalPhone(raw) {
  const trimmed = String(raw || "").trim();
  if (!trimmed) {
    throw new Error("Le numéro de téléphone est obligatoire.");
  }
  const e164 = tryNormalizePhoneToE164(trimmed);
  if (e164) return e164;
  throw new Error(
    "Indiquez le numéro local (ex. 06 12 34 56 78) ou le format international (+33…).",
  );
}

/**
 * Inscription / profil : pays + numéro local, ou « Autre pays » en +… direct.
 */
export function parsePhoneToE164({ countryId, value }) {
  const c = getPhoneCountryById(countryId);
  if (c.intlOnly) {
    return normalizeInternationalPhone(value);
  }
  return buildE164FromCountryAndNational(c, value);
}

/**
 * Saisie du numéro « national » seul, avec pays choisi à part (Select indicatif).
 * @param {{ dial: string, stripLeadingZero?: boolean }} meta - entrée pays (ex. getPhoneCountryById)
 */
export function formatNationalPhoneInputLive(meta, raw) {
  const c = meta;
  let s = String(raw ?? "");
  const trimmed = s.trim();

  // Collage +33… / 0033… → affichage national cohérent avec le pays sélectionné
  if (trimmed.startsWith("+") || trimmed.startsWith("00")) {
    let d = digitsOnly(trimmed);
    if (d.startsWith("00")) d = d.slice(2);
    if (d.startsWith(c.dial)) {
      let national = d.slice(c.dial.length);
      if (c.dial === "33" && national.length === 9 && /^[67]/.test(national)) {
        national = `0${national}`;
      }
      s = national;
    }
  }

  if (c.dial === "33") {
    return formatPhoneInputLive(s);
  }

  const dg = digitsOnly(s).slice(0, 15);
  if (!dg.length) return "";
  const chunks = dg.match(/.{1,3}/g) ?? [dg];
  return chunks.join(" ");
}

/**
 * Construit un E.164 à partir du pays et du champ national (sans indicatif).
 * @param {{ dial: string, name: string, stripLeadingZero?: boolean }} meta
 */
export function buildE164FromCountryAndNational(meta, nationalRaw) {
  const c = meta;
  if (c?.intlOnly) {
    return normalizeInternationalPhone(nationalRaw);
  }
  const t = String(nationalRaw || "").trim();

  let digits = digitsOnly(t);
  if (t.startsWith("+") || t.startsWith("00")) {
    let d = digitsOnly(t);
    if (d.startsWith("00")) d = d.slice(2);
    if (!d.startsWith(c.dial)) {
      throw new Error(`Ce numéro ne correspond pas au pays sélectionné (${c.name} +${c.dial}).`);
    }
    digits = d.slice(c.dial.length);
  }

  if (c.dial === "33" && digits.length === 9 && /^[67]/.test(digits)) {
    digits = `0${digits}`;
  }

  if (c.stripLeadingZero && digits.startsWith("0") && digits.length > 1) {
    digits = digits.slice(1);
  }

  if (digits.length < 8) {
    throw new Error("Numéro incomplet.");
  }
  if (digits.length > 15) {
    throw new Error("Numéro trop long.");
  }

  return normalizeInternationalPhone(`+${c.dial}${digits}`);
}
